import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { PlayIcon, CalculatorIcon, CheckCircleIcon, ListBulletIcon, Squares2X2Icon, Bars3Icon, DocumentTextIcon, DocumentArrowUpIcon, BookOpenIcon, LightBulbIcon, ChatBubbleLeftRightIcon, ChevronRightIcon, TrophyIcon, UserGroupIcon, ClockIcon, PuzzlePieceIcon, ChartBarIcon, SignalIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GameOutput, PlayOutput, DefaultService, Player } from '../generated-api';
import { useGames, useLeagues } from '../contexts/DataContext';
import PageHeader from '../components/common/PageHeader';
import ActionButtons from '../components/common/ActionButtons';
import PlayList from '../components/common/PlayList';
import SearchButton from '../components/common/SearchButton';
import RulesPage from './RulesPage';
import StrategyGuidePage from './StrategyGuidePage';
import FilesPage from './FilesPage';
import ChatPage from './ChatPage';
import { useJobPoller } from '../hooks/useJobPoller';

interface GameDetailPageProps {}

type SubPage = 'overview' | 'rules' | 'strategy' | 'files' | 'chat';

const GameDetailPage: React.FC<GameDetailPageProps> = () => {
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  // Get icon component based on field type
  const getFieldIcon = (type: string) => {
    switch (type) {
      case 'number':
        return CalculatorIcon;
      case 'boolean':
        return CheckCircleIcon;
      case 'select':
        return ListBulletIcon;
      case 'multiselect':
        return Squares2X2Icon;
      case 'order':
        return Bars3Icon;
      case 'text':
      default:
        return DocumentTextIcon;
    }
  };

  // Get type display name
  const getTypeDisplayName = (type: string): string => {
    switch (type) {
      case 'text':
        return 'Text';
      case 'number':
        return 'Number';
      case 'boolean':
        return 'Boolean (Yes/No)';
      case 'select':
        return 'Select (Single Choice)';
      case 'multiselect':
        return 'Multiselect (Multiple Choice)';
      case 'order':
        return 'Order (Team Position)';
      default:
        return type;
    }
  };
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { games, loading: gamesLoading, error: gamesError, fetch: fetchGames, invalidate: invalidateGames } = useGames();
  const { leagues, fetch: fetchLeagues } = useLeagues();
  const [game, setGame] = useState<GameOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [subPage, setSubPage] = useState<SubPage>('overview');
  const [plays, setPlays] = useState<PlayOutput[]>([]);
  const [playsLoading, setPlaysLoading] = useState(false);
  const [playsError, setPlaysError] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [playsOffset, setPlaysOffset] = useState(10);
  const [hasMorePlays, setHasMorePlays] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasRules, setHasRules] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const refreshHasRules = useCallback(() => {
    if (!id) return;
    DefaultService.getGameRulesGamesGameIdRulesGet({ gameId: id })
      .then(data => setHasRules(data.hasRules))
      .catch(() => {});
  }, [id]);

  const rulesPoller = useJobPoller(id || '', 'rules_processing', refreshHasRules);
  const strategyPoller = useJobPoller(id || '', 'strategy_generation', refreshHasRules);
  const isProcessingBackground = rulesPoller.isProcessing || strategyPoller.isProcessing;

  // Determine active sub-page from URL
  useEffect(() => {
    if (location.pathname.endsWith('/rules')) {
      setSubPage('rules');
    } else if (location.pathname.endsWith('/strategy')) {
      setSubPage('strategy');
    } else if (location.pathname.endsWith('/files')) {
      setSubPage('files');
    } else if (location.pathname.endsWith('/chat')) {
      setSubPage('chat');
    } else {
      setSubPage('overview');
      // Re-check for active jobs when returning to overview
      // (a sub-page like FilesPage may have started a job)
      rulesPoller.checkForJob();
      strategyPoller.checkForJob();
      refreshHasRules();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Load game data
  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Load leagues data
  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  useEffect(() => {
    if (id && games.length > 0) {
      const foundGame = games.find((g) => g.id === id);
      setGame(foundGame || null);
      setLoading(false);
    } else if (games.length > 0) {
      setLoading(false);
    }
  }, [id, games]);

  // Check if rules have been uploaded
  useEffect(() => {
    if (!id) return;
    DefaultService.getGameRulesGamesGameIdRulesGet({ gameId: id })
      .then(data => setHasRules(data.hasRules))
      .catch(() => {});
  }, [id]);

  // Fetch plays and players for the game
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setPlaysLoading(true);
      setPlaysError(null);
      try {
        const [playsData, playersData] = await Promise.all([
          DefaultService.getGamePlaysGamesGameIdPlaysGet({ gameId: id }),
          DefaultService.getPlayersPlayersGet()
        ]);
        setPlays(playsData);
        setPlayers(playersData);
        
        // Reset pagination
        setPlaysOffset(10);
        setHasMorePlays(playsData.length > 10);
      } catch (err) {
        console.error('Error fetching data:', err);
        setPlaysError('Failed to load play data');
      } finally {
        setPlaysLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Filter plays by search query
  const filteredPlays = useMemo(() => {
    if (!searchQuery.trim()) return plays;
    const query = searchQuery.toLowerCase();
    return plays.filter(play => {
      // Search in team names
      const teamNames = Object.keys(play.teams).join(' ').toLowerCase();
      if (teamNames.includes(query)) return true;
      // Search in player names
      const playerNames = Object.values(play.teams)
        .flat()
        .map(playerId => players.find(p => p.id === playerId)?.name || '')
        .join(' ')
        .toLowerCase();
      if (playerNames.includes(query)) return true;
      // Search in comment
      if (play.comment?.toLowerCase().includes(query)) return true;
      // Search in date
      if (play.playDate.toLowerCase().includes(query)) return true;
      return false;
    });
  }, [plays, players, searchQuery]);

  // Client-side pagination: slice plays array to show only current page
  const displayedPlays = useMemo(() => {
    return filteredPlays.slice(0, playsOffset);
  }, [filteredPlays, playsOffset]);

  const loadMorePlays = useCallback(() => {
    if (loadingMore || !hasMorePlays) return;

    setLoadingMore(true);
    setTimeout(() => {
      const newOffset = playsOffset + 10;
      setPlaysOffset(newOffset);
      setHasMorePlays(newOffset < filteredPlays.length);
      setLoadingMore(false);
    }, 100);
  }, [loadingMore, hasMorePlays, playsOffset, filteredPlays.length]);

  const refetchPlays = useCallback(async () => {
    if (!id) return;
    try {
      const playsData = await DefaultService.getGamePlaysGamesGameIdPlaysGet({ gameId: id });
      setPlays(playsData);
      setPlaysOffset(10);
      setHasMorePlays(playsData.length > 10);
    } catch (err) {
      console.error('Error refetching plays:', err);
    }
  }, [id]);

  // Calculate combined statistics by team count (average duration + highest score)
  const teamCountStatistics = useMemo(() => {
    // Calculate average duration by team count
    const playsWithDuration = plays.filter(
      (play) => play.durationMinutes != null && play.durationMinutes > 0
    );

    const durationByTeamCount: Record<number, { durations: number[]; playCount: number }> = {};
    
    playsWithDuration.forEach((play) => {
      const teamCount = Object.keys(play.teams).length;
      if (!durationByTeamCount[teamCount]) {
        durationByTeamCount[teamCount] = { durations: [], playCount: 0 };
      }
      durationByTeamCount[teamCount].durations.push(play.durationMinutes!);
      durationByTeamCount[teamCount].playCount++;
    });

    // Calculate highest scores by team count
    const playsWithScores = plays.filter((play) => {
      if (!play.teamMetadata) return false;
      return Object.values(play.teamMetadata).some(
        (metadata) => {
          if (!metadata || typeof metadata !== 'object') return false;
          // Check for both 'score' and 'Score' (case-insensitive)
          return 'score' in metadata || 'Score' in metadata;
        }
      );
    });

    const scoresByTeamCount: Record<number, { score: number; play: PlayOutput; teamName: string }> = {};
    
    playsWithScores.forEach((play) => {
      const teamCount = Object.keys(play.teams).length;
      
      // Find the team with the highest score in this play
      let highestScore = -Infinity;
      let highestScoreTeam = '';
      
      Object.entries(play.teamMetadata || {}).forEach(([teamName, metadata]) => {
        if (metadata && typeof metadata === 'object') {
          // Check for both 'score' and 'Score' (case-insensitive)
          const scoreValue = metadata.score !== undefined ? metadata.score : 
                           metadata.Score !== undefined ? metadata.Score : null;
          
          if (scoreValue !== null) {
            const score = typeof scoreValue === 'number' ? scoreValue : 
                         typeof scoreValue === 'string' ? parseFloat(scoreValue) : 0;
            
            if (!isNaN(score) && score > highestScore) {
              highestScore = score;
              highestScoreTeam = teamName;
            }
          }
        }
      });
      
      // Update best score for this team count if this is higher
      if (highestScore > -Infinity && 
          (!scoresByTeamCount[teamCount] || highestScore > scoresByTeamCount[teamCount].score)) {
        scoresByTeamCount[teamCount] = {
          score: highestScore,
          play,
          teamName: highestScoreTeam,
        };
      }
    });

    // Combine both metrics
    const allTeamCounts = new Set([
      ...Object.keys(durationByTeamCount).map(Number),
      ...Object.keys(scoresByTeamCount).map(Number)
    ]);

    const result = Array.from(allTeamCounts)
      .map((teamCount) => {
        const durationData = durationByTeamCount[teamCount];
        const scoreData = scoresByTeamCount[teamCount];

        let avgDuration: number | null = null;
        let playCount = 0;
        if (durationData) {
          avgDuration = Math.round(
            durationData.durations.reduce((sum, d) => sum + d, 0) / durationData.durations.length
          );
          playCount = durationData.playCount;
        }

        let highestScore: number | null = null;
        let winnerTeamName: string | null = null;
        let winnerPlayerNames: string | null = null;
        let playDate: string | null = null;
        let playId: string | null = null;
        let isSinglePlayerTeam: boolean = false;
        
        if (scoreData) {
          highestScore = scoreData.score;
          winnerTeamName = scoreData.teamName;
          const winnerPlayerIds = scoreData.play.teams[scoreData.teamName] || [];
          winnerPlayerNames = winnerPlayerIds
            .map(playerId => {
              const player = players.find(p => p.id === playerId);
              return player ? player.name : `Player ${playerId.slice(-4)}`;
            })
            .join(', ');
          playDate = scoreData.play.playDate;
          playId = scoreData.play.id;
          isSinglePlayerTeam = winnerPlayerIds.length === 1;
        }

        return {
          teamCount,
          avgDuration,
          playCount,
          highestScore,
          winnerTeamName,
          winnerPlayerNames,
          playDate,
          playId,
          isSinglePlayerTeam,
        };
      })
      .sort((a, b) => a.teamCount - b.teamCount);

    return result;
  }, [plays, players]);

  const handleDeleteGame = useCallback(async () => {
    if (!id || !game) return;

    const playCount = plays.length;
    const confirmMessage = playCount > 0
      ? `Are you sure you want to delete this game? This will NOT delete the ${playCount} recorded play sessions, but players will no longer be able to record new sessions for this game.`
      : 'Are you sure you want to delete this game? This action cannot be undone.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleting(true);
      await DefaultService.deleteGameGamesGameIdDelete({ gameId: id });
      invalidateGames();
      navigate('/games');
    } catch (err) {
      console.error('Error deleting game:', err);
      alert('Failed to delete game. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [id, game, plays.length, invalidateGames, navigate]);

  if (loading || (gamesLoading && games.length === 0)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (gamesError) {
    return (
        <div className="text-center py-12 text-red-500">
            <h2 className="text-2xl font-bold">Error loading game</h2>
            <p>{gamesError}</p>
            <button
                onClick={() => navigate('/games')}
                className="mt-4 text-primary-600 hover:text-primary-800"
            >
                ← Back to Games
            </button>
        </div>
    );
  }

  if (!game) {
    return (
        <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-heading">Game not found</h2>
            <button
                onClick={() => navigate('/games')}
                className="mt-4 text-primary-600 hover:text-primary-800"
            >
                ← Back to Games
            </button>
        </div>
    );
  }

  // Sub-page back header for non-overview pages
  const renderSubPageHeader = (title: string) => (
    <PageHeader className="mb-6 px-4 sm:px-0">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <button
            data-track="game_back_to_details"
            onClick={() => navigate(`/games/${id}`)}
            className="text-primary-600 hover:text-primary-800 mb-2"
          >
            ← Back to {game.name}
          </button>
          <h1 className="text-fluid-h1 font-bold text-heading">{title}</h1>
        </div>
      </div>
    </PageHeader>
  );

  // Render sub-pages (rules, strategy, chat, files) as full-page views
  if (subPage !== 'overview') {
    return (
      <div className="container mx-auto px-0 py-4 sm:px-4 sm:py-8">
        {subPage === 'files' && (
          <>
            {renderSubPageHeader('Files')}
            <FilesPage gameId={id!} gameName={game.name} />
          </>
        )}
        {subPage === 'rules' && (
          <>
            {renderSubPageHeader('Rules Summary')}
            <RulesPage gameId={id!} gameName={game.name} />
          </>
        )}
        {subPage === 'strategy' && (
          <>
            {renderSubPageHeader('Strategy Guide')}
            <StrategyGuidePage gameId={id!} gameName={game.name} />
          </>
        )}
        {subPage === 'chat' && (
          <>
            {renderSubPageHeader('AI Chat')}
            <ChatPage gameId={id!} gameName={game.name} />
          </>
        )}
      </div>
    );
  }

  const aiLinks = [
    { to: `/games/${id}/files`, icon: DocumentArrowUpIcon, label: 'Files', description: 'Upload and manage rules', alwaysEnabled: true },
    { to: `/games/${id}/rules`, icon: BookOpenIcon, label: 'Rules Summary', description: 'AI-generated rules overview', alwaysEnabled: false },
    { to: `/games/${id}/strategy`, icon: LightBulbIcon, label: 'Strategy Guide', description: 'Beginner to advanced strategies', alwaysEnabled: false },
    { to: `/games/${id}/chat`, icon: ChatBubbleLeftRightIcon, label: 'AI Chat', description: 'Ask questions about the rules', alwaysEnabled: false },
  ];

  return (
    <div className="container mx-auto px-0 py-4 sm:px-4 sm:py-8">
      {/* Back link */}
      <button
        onClick={() => navigate('/games')}
        className="text-primary-600 hover:text-primary-800 mb-4 text-sm font-medium px-4 sm:px-0"
      >
        ← Back to Games
      </button>

      {/* Game Hero Card */}
      <div className="bg-surface sm:shadow-sm sm:rounded-xl sm:border sm:border-edge overflow-hidden mb-4 sm:mb-8 relative">
        {/* Top section: image + title + actions */}
        <div className="p-4 sm:p-6">
          <div className="flex gap-4 sm:gap-6">
            {/* Game Image */}
            {game.imageUrl ? (
              <img
                src={game.imageUrl}
                alt={game.name}
                className="w-20 h-20 sm:w-32 sm:h-32 object-contain rounded-xl bg-surface-base flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-xl bg-surface-subtle flex items-center justify-center flex-shrink-0">
                <span className="text-2xl sm:text-4xl text-faint">🎲</span>
              </div>
            )}

            {/* Title + meta + actions */}
            <div className="flex-1 min-w-0">
              <h1 className="text-fluid-h1 font-bold text-heading leading-tight sm:pr-28">{game.name}</h1>
              <div className="hidden sm:block absolute top-4 right-4 sm:top-6 sm:right-6">
                <ActionButtons
                  editLink={`/games/${id}/edit`}
                  onDelete={handleDeleteGame}
                  editLabel="Edit Game"
                  deleteLabel="Delete Game"
                  deleteDisabled={deleting}
                  className="flex-row"
                />
              </div>

              {/* Metadata pills */}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-subtle text-body">
                  <UserGroupIcon className="h-3.5 w-3.5" />
                  {game.minTeams}–{game.maxTeams} players
                </span>
                {((game as any).durationMinutes || (game as any).duration) && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-subtle text-body">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {formatDuration((game as any).durationMinutes || (game as any).duration)}
                  </span>
                )}
                {game.owners && game.owners.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-subtle text-body">
                    📦 {game.owners.join(', ')}
                  </span>
                )}
              </div>

              {/* Tags - inline on desktop, hidden on mobile (shown below) */}
              {game.tags && game.tags.length > 0 && (
                <div className="hidden sm:flex flex-wrap gap-1.5 mt-3">
                  {game.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tags - mobile only */}
          {game.tags && game.tags.length > 0 && (
            <div className="sm:hidden flex flex-wrap gap-1.5 mt-3">
              {game.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Action buttons - mobile only */}
          <div className="sm:hidden flex gap-2 mt-3">
            <ActionButtons
              editLink={`/games/${id}/edit`}
              onDelete={handleDeleteGame}
              editLabel="Edit Game"
              deleteLabel="Delete Game"
              deleteDisabled={deleting}
              className="flex-row"
            />
          </div>
        </div>

        {/* Processing indicator */}
        {isProcessingBackground && (
          <div className="border-t border-primary-100 bg-primary-50 px-4 sm:px-6 py-3">
            <div className="flex items-center gap-2 text-sm text-primary-700">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent flex-shrink-0"></div>
              <span>{rulesPoller.statusText || strategyPoller.statusText || 'Processing...'}</span>
            </div>
          </div>
        )}

        {/* Extensions */}
        {game.availableExtensions && game.availableExtensions.length > 0 && (
          <div className="border-t border-edge-subtle px-4 sm:px-6 py-3">
            <div className="flex items-start gap-2">
              <PuzzlePieceIcon className="h-4 w-4 text-disabled mt-0.5 flex-shrink-0" />
              <div className="flex flex-wrap gap-1.5">
                {game.availableExtensions.map((ext) => (
                  <span
                    key={ext}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100"
                  >
                    {ext}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        {game.description && (
          <div className="border-t border-edge-subtle px-4 sm:px-6 py-4">
            <div className={`prose prose-sm max-w-none text-body ${!descriptionExpanded ? 'line-clamp-3' : ''}`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {game.description}
              </ReactMarkdown>
            </div>
            <button
              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              className="mt-1 text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              {descriptionExpanded ? 'Show less' : 'Show more'}
            </button>
          </div>
        )}

        {/* Custom Player Metadata Fields */}
        {game.playerMetadataFields && game.playerMetadataFields.length > 0 && (
          <div className="border-t border-edge-subtle px-4 sm:px-6 py-4">
            <h3 className="text-sm font-medium text-muted mb-4">Custom Player Metadata Fields</h3>
            <div className="space-y-3">
              {game.playerMetadataFields.map((field, index) => {
                const Icon = getFieldIcon(field.type);
                return (
                  <div
                    key={index}
                    className="border border-edge rounded-lg p-4 bg-surface-base"
                  >
                    <div className="flex items-start space-x-3">
                      <Icon className="w-5 h-5 text-body mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-heading">
                            {field.displayName}
                          </h4>
                          <span className="text-xs text-muted bg-surface px-2 py-1 rounded border border-edge">
                            {getTypeDisplayName(field.type)}
                          </span>
                        </div>
                        {field.options && field.options.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted mb-1">
                              {field.type === 'text'
                                ? 'Suggested options:'
                                : field.type === 'select' || field.type === 'multiselect'
                                ? 'Available options:'
                                : 'Options:'}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {field.options.map((option, optIndex) => (
                                <span
                                  key={optIndex}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                                >
                                  {option}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Statistics by Team Count */}
        {teamCountStatistics.length > 0 && (
          <div className="border-t border-edge-subtle px-4 sm:px-6 py-4">
            <h3 className="text-sm font-medium text-muted mb-3">Play Statistics by Team Count</h3>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted">Teams</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted">Avg Duration</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted">High Score</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted">Plays</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge-subtle">
                  {teamCountStatistics.map((item) => (
                    <tr key={item.teamCount}>
                      <td className="px-3 py-2.5 font-medium text-heading font-mono">
                        {item.teamCount}
                      </td>
                      <td className="px-3 py-2.5 text-body">
                        {item.avgDuration !== null ? formatDuration(item.avgDuration) : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-body">
                        {item.highestScore !== null ? (
                          <div>
                            {item.playId ? (
                              <Link
                                data-track="game_top_score_play"
                                to={`/plays/${item.playId}`}
                                className="font-semibold font-mono text-primary-600 hover:text-primary-800 hover:underline"
                              >
                                {item.highestScore}
                              </Link>
                            ) : (
                              <span className="font-semibold font-mono">{item.highestScore}</span>
                            )}
                            {item.winnerPlayerNames && (
                              <div className="text-muted text-xs mt-0.5 truncate max-w-[120px] sm:max-w-none">
                                {(() => {
                                  if (item.isSinglePlayerTeam && item.winnerTeamName === item.winnerPlayerNames) {
                                    return item.winnerPlayerNames;
                                  } else if (item.winnerTeamName) {
                                    return (
                                      <>
                                        {item.winnerTeamName}
                                        {item.winnerPlayerNames && ` (${item.winnerPlayerNames})`}
                                      </>
                                    );
                                  } else {
                                    return item.winnerPlayerNames;
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-muted font-mono">
                        {item.playCount > 0 ? item.playCount : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mx-4 sm:mx-0 mb-4 sm:mb-8">
        <Link
          data-track="game_start_live_play"
          to={`/plays/new?gameId=${id}&mode=live`}
          className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 sm:py-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          <SignalIcon className="h-5 w-5" />
          <span className="text-xs sm:text-sm">Start Live Play</span>
        </Link>
        <Link
          data-track="game_record_play"
          to={`/plays/new?gameId=${id}`}
          className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 sm:py-4 rounded-xl text-sm font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 dark:bg-primary-600/10 dark:hover:bg-primary-600/20 transition-colors"
        >
          <PlayIcon className="h-5 w-5" />
          <span className="text-xs sm:text-sm">Record Play</span>
        </Link>
        <Link
          data-track="game_view_league"
          to={`/leagues/game-${id}`}
          className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 sm:py-4 rounded-xl text-sm font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 dark:bg-primary-600/10 dark:hover:bg-primary-600/20 transition-colors"
        >
          <ChartBarIcon className="h-5 w-5" />
          <span className="text-xs sm:text-sm">Game Stats</span>
        </Link>
      </div>

      {/* Game Rankings */}
      {plays.length > 0 && (
        <Link
          data-track="game_league_card"
          to={`/leagues/game-${id}`}
          className="flex items-center justify-between bg-surface px-4 py-4 sm:shadow sm:rounded-xl sm:px-6 sm:border sm:border-edge hover:bg-surface-hover transition-colors mb-4 sm:mb-8"
        >
          <div className="flex items-center gap-3">
            <TrophyIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
            <div>
              <h2 className="text-fluid-h2 font-semibold text-heading">Game Rankings</h2>
              <p className="text-xs text-muted">View full leaderboard, rating history & stats</p>
            </div>
          </div>
          <ChevronRightIcon className="h-5 w-5 text-disabled flex-shrink-0" />
        </Link>
      )}

      {/* AI & Rules Section */}
      <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-xl sm:p-6 mb-4 sm:mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-fluid-h2 font-semibold text-heading">AI & Rules</h2>
          {isProcessingBackground && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent"></div>
              <span>{rulesPoller.statusText || strategyPoller.statusText || 'Processing...'}</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {aiLinks.map(({ to, icon: LinkIcon, label, description, alwaysEnabled }) => {
            const enabled = alwaysEnabled || hasRules;
            return enabled ? (
              <Link
                data-track="game_tool_link"
                key={to}
                to={to}
                className="flex items-center gap-4 p-4 rounded-lg border border-edge hover:bg-surface-hover transition-all active:scale-[0.98]"
              >
                <LinkIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-heading">{label}</p>
                  <p className="text-xs text-muted">{description}</p>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-disabled flex-shrink-0" />
              </Link>
            ) : (
              <div
                key={to}
                className="flex items-center gap-4 p-4 rounded-lg border border-edge-subtle bg-surface-base opacity-50"
              >
                <LinkIcon className="h-6 w-6 text-disabled flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-disabled">{label}</p>
                  <p className="text-xs text-disabled">Upload rules first</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Play Sessions */}
      <div>
        <div className="mb-4 px-4 sm:px-0">
          <div className="flex items-center justify-between">
            <h2 className="text-fluid-h2 font-semibold text-heading">Play Sessions</h2>
            <SearchButton
              isExpanded={searchExpanded}
              onToggle={() => setSearchExpanded(!searchExpanded)}
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search plays..."
            />
          </div>
          <p className="text-body text-sm mt-1">
            {searchQuery ? (
              <>Showing {filteredPlays.length} of {plays.length} play sessions</>
            ) : (
              <>All play sessions recorded for this game</>
            )}
          </p>
        </div>
        {playsLoading ? (
          <div className="bg-surface px-4 py-6 sm:shadow sm:rounded-xl sm:p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-2 text-body">Loading plays...</p>
          </div>
        ) : playsError ? (
          <div className="bg-surface px-4 py-6 sm:shadow sm:rounded-xl sm:p-6 text-center text-red-500">
            <p>{playsError}</p>
          </div>
        ) : displayedPlays.length === 0 ? (
          <div className="bg-surface px-4 py-6 sm:shadow sm:rounded-xl sm:p-6 text-center text-muted">
            <p>{searchQuery ? 'No play sessions match your search.' : 'No play sessions recorded yet for this game.'}</p>
          </div>
        ) : (
          <>
            <PlayList
              plays={displayedPlays}
              players={players}
              games={games}
              leagues={leagues}
              showActions={false}
              showPlayerDetails={true}
              showFullDetails={true}
              showPlayImages={false}
              onReactionChange={refetchPlays}
            />
            {hasMorePlays && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMorePlays}
                  disabled={loadingMore}
                  className="bg-primary-500 hover:bg-primary-700 disabled:bg-primary-300 text-white font-medium py-2 px-6 rounded-lg transition-colors inline-flex items-center space-x-2"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Show More Plays</span>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GameDetailPage;
